import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Mail, Phone, Clock, CheckCircle, AlertCircle, 
  MessageSquare, Send, Trash2, RefreshCw, Loader2, User, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, in_progress, resolved
  const [countyFilter, setCountyFilter] = useState('all'); // all, osceola, orange, polk
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = currentUser.role === 'owner';
  const userName = currentUser.name || 'Admin';

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/county-inquiries/all`);
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      }
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/county-inquiries/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  useEffect(() => {
    fetchInquiries();
    fetchStats();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/county-inquiries/${id}/status?status=${status}`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Status updated');
        fetchInquiries();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim() || !selectedInquiry) return;
    
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/county-inquiries/${selectedInquiry.id}/respond?admin_name=${encodeURIComponent(userName)}&response=${encodeURIComponent(responseText)}`,
        { method: 'POST' }
      );
      if (response.ok) {
        toast.success('Response sent');
        setResponseText('');
        fetchInquiries();
        // Update selected inquiry
        const updated = inquiries.find(i => i.id === selectedInquiry.id);
        if (updated) {
          setSelectedInquiry({
            ...updated,
            responses: [...(updated.responses || []), { admin_name: userName, response: responseText, created_at: new Date().toISOString() }]
          });
        }
      }
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/county-inquiries/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Inquiry deleted');
        fetchInquiries();
        fetchStats();
        if (selectedInquiry?.id === id) setSelectedInquiry(null);
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredInquiries = inquiries.filter(inq => {
    if (filter !== 'all' && inq.status !== filter) return false;
    if (countyFilter !== 'all' && !inq.county.toLowerCase().includes(countyFilter)) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      resolved: 'bg-green-100 text-green-700'
    };
    const labels = {
      new: 'New',
      in_progress: 'In Progress',
      resolved: 'Resolved'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {labels[status] || 'New'}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-inquiries-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-charcoal flex items-center gap-3">
            <MapPin className="text-amber-600" />
            County Inquiries
          </h1>
          <p className="text-charcoal/60 text-sm mt-1">
            {isOwner ? 'View all county inquiries' : `Inquiries assigned to you`}
          </p>
        </div>
        <button
          onClick={() => { fetchInquiries(); fetchStats(); }}
          className="px-4 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors text-sm flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
            <div className="text-xs text-blue-800">New</div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{stats.in_progress}</div>
            <div className="text-xs text-amber-800">In Progress</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            <div className="text-xs text-green-800">Resolved</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.total}</div>
            <div className="text-xs text-purple-800">Total</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-charcoal/50" />
          <span className="text-sm text-charcoal/70">Status:</span>
          {['all', 'new', 'in_progress', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === s ? 'bg-charcoal text-white' : 'bg-cream text-charcoal hover:bg-gold/20'
              }`}
            >
              {s === 'all' ? 'All' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-charcoal/50" />
          <span className="text-sm text-charcoal/70">County:</span>
          {['all', 'osceola', 'orange', 'polk'].map(c => (
            <button
              key={c}
              onClick={() => setCountyFilter(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                countyFilter === c ? 'bg-amber-600 text-white' : 'bg-cream text-charcoal hover:bg-gold/20'
              }`}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inquiries List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-amber-600" size={32} />
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-20 bg-cream/50 rounded-2xl">
              <MapPin size={48} className="mx-auto text-charcoal/30 mb-4" />
              <p className="text-charcoal/60">No inquiries found</p>
            </div>
          ) : (
            filteredInquiries.map((inquiry) => (
              <motion.div
                key={inquiry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSelectedInquiry(inquiry)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedInquiry?.id === inquiry.id 
                    ? 'border-amber-400 ring-2 ring-amber-100' 
                    : 'border-gold/10'
                } ${inquiry.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h4 className="font-medium text-charcoal">{inquiry.name}</h4>
                    <p className="text-xs text-charcoal/50">{inquiry.email}</p>
                  </div>
                  {getStatusBadge(inquiry.status)}
                </div>
                
                <p className="text-sm font-medium text-charcoal mb-1">{inquiry.subject}</p>
                <p className="text-sm text-charcoal/70 line-clamp-2 mb-3">{inquiry.message}</p>
                
                <div className="flex items-center justify-between text-xs text-charcoal/50">
                  <div className="flex items-center gap-2">
                    <MapPin size={12} />
                    <span>{inquiry.county}</span>
                    <span>•</span>
                    <span>{inquiry.assigned_to}</span>
                  </div>
                  <span>{formatDate(inquiry.created_at)}</span>
                </div>

                {inquiry.responses?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gold/10 text-xs text-charcoal/50 flex items-center gap-1">
                    <MessageSquare size={12} />
                    {inquiry.responses.length} response(s)
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:sticky lg:top-6 h-fit">
          {selectedInquiry ? (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl border border-gold/10 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-amber-50 p-4 border-b border-gold/10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif text-lg text-charcoal">{selectedInquiry.subject}</h3>
                  {getStatusBadge(selectedInquiry.status)}
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="flex items-center gap-1 text-charcoal/70">
                    <User size={14} />
                    {selectedInquiry.name}
                  </span>
                  <a href={`mailto:${selectedInquiry.email}`} className="flex items-center gap-1 text-amber-700 hover:underline">
                    <Mail size={14} />
                    {selectedInquiry.email}
                  </a>
                  {selectedInquiry.phone && (
                    <a href={`tel:${selectedInquiry.phone}`} className="flex items-center gap-1 text-amber-700 hover:underline">
                      <Phone size={14} />
                      {selectedInquiry.phone}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-charcoal/50">
                  <MapPin size={12} />
                  {selectedInquiry.county} • Assigned to {selectedInquiry.assigned_to}
                </div>
              </div>

              {/* Message */}
              <div className="p-4 border-b border-gold/10">
                <p className="text-charcoal/80 whitespace-pre-wrap">{selectedInquiry.message}</p>
                <p className="text-xs text-charcoal/50 mt-3">{formatDate(selectedInquiry.created_at)}</p>
              </div>

              {/* Responses */}
              {selectedInquiry.responses?.length > 0 && (
                <div className="p-4 border-b border-gold/10 bg-cream/30 max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-medium text-charcoal mb-3">Responses</h4>
                  <div className="space-y-3">
                    {selectedInquiry.responses.map((resp, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-gold/10">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-amber-700">{resp.admin_name}</span>
                          <span className="text-xs text-charcoal/50">{formatDate(resp.created_at)}</span>
                        </div>
                        <p className="text-sm text-charcoal/80">{resp.response}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Response */}
              <div className="p-4 border-b border-gold/10">
                <label className="text-sm font-medium text-charcoal mb-2 block">Add Response</label>
                <textarea
                  rows={3}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Write your response..."
                  className="w-full px-3 py-2 rounded-lg border border-gold/20 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 resize-none"
                />
                <button
                  onClick={handleSendResponse}
                  disabled={sending || !responseText.trim()}
                  className="mt-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send Response
                </button>
              </div>

              {/* Actions */}
              <div className="p-4 flex flex-wrap gap-2">
                <select
                  value={selectedInquiry.status}
                  onChange={(e) => handleStatusChange(selectedInquiry.id, e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gold/20 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                >
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
                <button
                  onClick={() => handleDelete(selectedInquiry.id)}
                  className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm flex items-center gap-1 transition-colors"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="bg-cream/50 rounded-2xl p-12 text-center">
              <MessageSquare size={48} className="mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50">Select an inquiry to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
